import { useRef, useState } from 'react'
import { Button, Group, IconButton, iconSizes, Modal, Select, Stack, Text } from '@common/ui'
import { IconTrash } from '@common/ui/icons'
import { com, kotlin } from '@eidu/web'
import PathPermission from '../../pages/permission/path/PathPermission'
import FieldValueEqualsInput from '../input/FieldValueEqualsInput'
import FieldValueEqualsUserFieldValueInput from '../input/FieldValueEqualsUserFieldValueInput'
import FieldValueEqualsUserEntityIdInput from '../input/FieldValueEqualsUserEntityIdInput'
import SingleInput from '../input/SingleInput'
import { EDITOR_MODE_OPTIONS, EditorMode, editorModeOf } from './utils'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntitySelector = com.eidu.entity.selector.EntitySelector
import EntityPath = com.eidu.entity.path.EntityPath
import findPathsBetweenTypes = com.eidu.entity.path.findPathsBetweenTypes
import EntityAction = com.eidu.entity.permission.EntityAction
import EntitySelectorPermission = com.eidu.entity.permission.EntitySelectorPermission
import EntityPathPermission = com.eidu.entity.permission.EntityPathPermission
import Permission = com.eidu.entity.permission.Permission
import KtList = kotlin.collections.KtList

type PermissionEditorRowProps = {
  permission: Permission
  allowsPath: boolean
  userEntityTypeId: EntityTypeId | undefined
  entityTypes: readonly EntityType[]
  rowEntityTypeId: EntityTypeId
  action: EntityAction
  onChange: (permission: Permission) => void
  onDelete: () => void
}

const PermissionEditorRow = ({
  permission,
  allowsPath,
  userEntityTypeId,
  entityTypes,
  rowEntityTypeId,
  action,
  onChange,
  onDelete,
}: PermissionEditorRowProps) => {
  const [mode, setMode] = useState<EditorMode>(editorModeOf(permission))
  const [confirmClearOpen, setConfirmClearOpen] = useState(false)
  const pendingModeRef = useRef<EditorMode | null>(null)

  const allowedOptions = allowsPath ? EDITOR_MODE_OPTIONS : EDITOR_MODE_OPTIONS.filter((o) => o.value !== 'path')

  const applyMode = (next: EditorMode) => {
    setMode(next)
    if (next === 'all') {
      onChange(new EntitySelectorPermission(action, new EntitySelector.OfType(rowEntityTypeId)))
      return
    }
    if (next === 'path' && userEntityTypeId) {
      const paths = findPathsBetweenTypes(
        KtList.fromJsArray(entityTypes),
        userEntityTypeId,
        rowEntityTypeId
      ).asJsReadonlyArrayView()
      if (paths.length > 0) onChange(new EntityPathPermission(action, paths[0]))
    }
    // For fve / fveUserField / fveUserEntity, the corresponding sub-input
    // commits the new permission once the user fills it in.
  }

  const handleModeChange = (next: EditorMode) => {
    if (next === mode) return
    if (mode === 'path' && next !== 'path' && permission instanceof EntityPathPermission) {
      pendingModeRef.current = next
      setConfirmClearOpen(true)
      return
    }
    applyMode(next)
  }

  const singleSelector =
    permission instanceof EntitySelectorPermission && permission.targets instanceof EntitySelector.Single
      ? permission.targets
      : undefined
  const fveSelector =
    permission instanceof EntitySelectorPermission && permission.targets instanceof EntitySelector.FieldValueEquals
      ? permission.targets
      : undefined
  const fveUserFieldSelector =
    permission instanceof EntitySelectorPermission &&
    permission.targets instanceof EntitySelector.FieldValueEqualsUserFieldValue
      ? permission.targets
      : undefined
  const fveUserEntitySelector =
    permission instanceof EntitySelectorPermission &&
    permission.targets instanceof EntitySelector.FieldValueEqualsUserEntityId
      ? permission.targets
      : undefined

  return (
    <Stack
      gap="xs"
      p="xs"
      style={{
        border: '1px solid var(--trellis-color-outline)',
        borderRadius: 'var(--trellis-radius-xs)',
        backgroundColor: 'var(--trellis-color-surface)',
      }}
    >
      <Group gap="xs" align="center" wrap="wrap">
        <Select
          label="Filter"
          size="sm"
          style={{ minWidth: 200, flexShrink: 0 }}
          value={mode}
          onChange={(value) => {
            if (value !== null) handleModeChange(value as EditorMode)
          }}
          data={allowedOptions.map((option) => ({ value: option.value, label: option.label }))}
          data-testid="permission-mode-select"
          allowDeselect={false}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {mode === 'path' ? (
            <PathPermission
              fromEntityTypeId={userEntityTypeId}
              preselectedPath={permission instanceof EntityPathPermission ? permission.path : undefined}
              entityTypes={entityTypes}
              onChange={(path: EntityPath | undefined) => {
                if (path) onChange(new EntityPathPermission(action, path))
              }}
              lockedToTypeId={rowEntityTypeId}
            />
          ) : mode === 'single' ? (
            <SingleInput
              entityTypeId={rowEntityTypeId}
              selector={singleSelector}
              setSelector={(s) => onChange(new EntitySelectorPermission(action, s))}
              disabled={false}
            />
          ) : mode === 'fve' ? (
            <FieldValueEqualsInput
              entityTypeId={rowEntityTypeId}
              selector={fveSelector}
              setSelector={(s) => onChange(new EntitySelectorPermission(action, s))}
              disabled={false}
            />
          ) : mode === 'fveUserField' ? (
            <FieldValueEqualsUserFieldValueInput
              entityTypeId={rowEntityTypeId}
              allEntityTypes={entityTypes}
              selector={fveUserFieldSelector}
              setSelector={(s) => onChange(new EntitySelectorPermission(action, s))}
              disabled={false}
            />
          ) : mode === 'fveUserEntity' ? (
            <FieldValueEqualsUserEntityIdInput
              entityTypeId={rowEntityTypeId}
              selector={fveUserEntitySelector}
              setSelector={(s) => onChange(new EntitySelectorPermission(action, s))}
              disabled={false}
            />
          ) : null}
        </div>
        <IconButton size="sm" onClick={onDelete} aria-label="Remove permission">
          <IconTrash size={iconSizes.sm} stroke={1.5} />
        </IconButton>
      </Group>
      <Modal opened={confirmClearOpen} onClose={() => setConfirmClearOpen(false)} title="Clear assignment?">
        <Text>Switching the filter will discard the current assignment. Are you sure?</Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setConfirmClearOpen(false)}>
            Cancel
          </Button>
          <Button
            color="negative"
            onClick={() => {
              setConfirmClearOpen(false)
              const next = pendingModeRef.current ?? 'all'
              pendingModeRef.current = null
              applyMode(next)
            }}
          >
            Clear assignment
          </Button>
        </Group>
      </Modal>
    </Stack>
  )
}

export default PermissionEditorRow
